import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from "react-router-dom";
import client from '../../sanity/client';
import './CheminPage.scss';
import { ArrowLeft, ArrowRight } from '../../assets/svg';
import { useLanguage } from '../../components/LanguageContext';
import SubPageChemin from './SubPage';
import Expositions from './Expositions';

const CheminPage = () => {
  const [data, setData] = useState(null);
  const [subPages, setSubPages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false); // Changé de true à false
  const { language } = useLanguage();
  const { hash } = useLocation();
  const location = useLocation();

  const intervalRef = useRef(null);
  
  useEffect(() => {
    if (location.state?.scrollTo) {
      const scrollToId = location.state.scrollTo;
      const tryScroll = () => {
        const el = document.getElementById(scrollToId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else {
          // Attente si le DOM n’est pas prêt
          setTimeout(tryScroll, 100);
        }
      };
      tryScroll();
    }
  }, [subPages]); // Attendez que subPages soient montés avant de scroller
  
  useEffect(() => {
    if (hash) {
      // Attendre que le DOM ait rendu les sections
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100); // 100ms de délai pour laisser le temps au composant de se monter
    }
  }, [hash, subPages]); // Assurez-vous que `subPages` est bien défini dans ce composant
  // Fonction pour démarrer l’auto-défilement
  const startAutoSlide = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % (data?.carrousel?.length || 1));
    }, 3000);
  };

  // Remet le timer à 0 quand on change manuellement
  const resetAutoSlide = () => {
    startAutoSlide();
  };
  useEffect(() => {
  // ✅ Gérer la restauration de scroll depuis les détails d'exposition
  if (location.state?.scrollTo === 'expositions' && location.state?.preserveScroll) {
    const tryRestoreScroll = () => {
      const savedPosition = sessionStorage.getItem('expositionScrollPosition');
      const expositionsElement = document.getElementById('expositions');
      
      if (savedPosition && expositionsElement) {
        const scrollPosition = parseInt(savedPosition, 10);
        window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        sessionStorage.removeItem('expositionScrollPosition');
        return true;
      }
      return false;
    };

    const maxRetries = 10;
    let retryCount = 0;
    
    const retryRestore = () => {
      if (tryRestoreScroll() || retryCount >= maxRetries) {
        return;
      }
      retryCount++;
      setTimeout(retryRestore, 100);
    };

    setTimeout(retryRestore, 100);
  }
}, [location.state, subPages, data]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const queryPage = `*[_type == "chemin"][0]{
          title,
          carrousel[]{ "imageUrl": asset->url + "?auto=format" }
        }`;
        const resultPage = await client.fetch(queryPage);
        
        // ✅ Correction ici : récupérer les objets title et content
        const querySubPages = `*[_type == "subPageChemin"] | order(order asc) {
          _id,
          title,
          "slug": slug.current,
          "imageUrl": image.asset->url,
          content
        }`;
        
        const resultSubPages = await client.fetch(querySubPages);
        console.log('RAW DATA FROM SANITY:', resultSubPages);
        
        setData(resultPage);
        setSubPages(resultSubPages);
        console.log('SubPages avec langues:', resultSubPages); // Pour débugger
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
      } finally {
        // S'assurer que l'animation se termine même en cas d'erreur
        setIsAnimating(false);
      }
    };
  
    fetchData();
  }, []);
  
  useEffect(() => {
    if (data?.carrousel?.length > 0) {
      startAutoSlide();
    }
    return () => clearInterval(intervalRef.current);
  }, [data]);

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash]);

  const goToNextSlide = () => {
    if (data && data.carrousel && data.carrousel.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % data.carrousel.length);
      resetAutoSlide();
    }
  };

  const goToPrevSlide = () => {
    if (data && data.carrousel && data.carrousel.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + data.carrousel.length) % data.carrousel.length);
      resetAutoSlide();
    }
  };

  return (
    <div id="chemin" className="chemin-page titre-page">
      <section className="carrousel-page" id="carousel">
      <h1 className="chemin-text" data-aos="fade-in" data-aos-duration="800">
        {data?.title?.[language] || data?.title?.fr || data?.title?.en || 'Chemin'}
      </h1>
      
      {data && data.carrousel && data.carrousel.length > 0 && (
          <div className="carousel" data-aos="fade-in" data-aos-duration="800" data-aos-delay="200">
            <div className="carousel-image-container">
              {data.carrousel.map((image, index) => (
                <img
                  key={index}
                  src={image.imageUrl}
                  alt={`Slide ${index + 1}`}
                  className={`carousel-image ${index === currentIndex ? 'active' : ''}`}
                />
              ))}
            </div>

            <div className="carousel-controls">
              <button className="carousel-control prev" onClick={goToPrevSlide}>
                <ArrowLeft />
              </button>
              <button className="carousel-control next" onClick={goToNextSlide}>
                <ArrowRight />
              </button>
            </div>
          </div>
        )}
      </section>

            {subPages.map((subPage, index) => (
        <section key={subPage._id} id={subPage.slug} className="sub-page-section" data-aos="fade-zoom-in"
     data-aos-easing="ease-in-back"
     data-aos-offset="0" data-aos-delay={index * 200}>
          <SubPageChemin data={subPage} />
        </section>
      ))}
      <section 
        id="expositions" 
        data-aos="fade-in"
     data-aos-easing="ease-in-back"
     data-aos-delay="1000"
     data-aos-offset="0"
      >
        <Expositions />
      </section>
    </div>
  );
};

export default CheminPage;